import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppShell, PageHeader } from "@/components/AppShell";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Loader2,
	Upload,
	Smartphone,
	PanelLeftOpen,
	CheckCircle2,
	AlertCircle,
	X,
	GripVertical,
	Film,
	Images,
	Search,
	Play,
	Link2,
	Rocket,
} from "lucide-react";
import { toast } from 'sonner';
import { fetchDevices, type VMOSDevice } from '@/services/vmos-api';
import { uploadFiles } from '@/services/api';
import { listSlackBots, type SlackBot } from '@/services/slack-bots-api';
import {
	startMultiContentSession,
	ingestLink,
	type ContentFileRef,
	type ContentPlatform,
	type ContentType,
	type YouTubeFormat,
} from '@/services/content-api';
import { cn } from '@/lib/utils';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DEFAULT_LLM_MODEL = "gemini-3-flash-preview";
const DEFAULT_COMMENT_MODEL = "gemini-3-flash-preview";

// === Types ===

type FileStatus = 'idle' | 'uploading' | 'done' | 'error';
type SourceMode = 'files' | 'link';

interface OrderedFile {
	id: string;
	file: File;
	previewUrl: string;
	status: FileStatus;
	error?: string;
}

// === Sortable Image Row ===

function SortableImageRow({
	file,
	index,
	isUploading,
	onRemove,
}: {
	file: OrderedFile;
	index: number;
	isUploading: boolean;
	onRemove: (id: string) => void;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: file.id, disabled: isUploading });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="flex items-center gap-2 p-2 border rounded-lg bg-background"
		>
			<button
				type="button"
				className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-foreground touch-none"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="h-4 w-4" />
			</button>
			<span className="text-xs font-mono text-muted-foreground w-6 text-center">
				{index + 1}
			</span>
			<img
				src={file.previewUrl}
				alt={file.file.name}
				className="h-10 w-10 object-cover rounded"
			/>
			<FileStatusIcon status={file.status} />
			<span className="text-sm truncate flex-1">{file.file.name}</span>
			{file.status !== 'uploading' && (
				<button
					onClick={() => onRemove(file.id)}
					className="p-1 text-muted-foreground hover:text-destructive"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			)}
		</div>
	);
}

// === Content Upload Page ===

export default function ContentUploadPage() {
	const navigate = useNavigate();
	const [contentType, setContentType] = useState<ContentType>('video');
	const [platformsByDevice, setPlatformsByDevice] = useState<Record<string, ContentPlatform[]>>({});
	const [sourceMode, setSourceMode] = useState<SourceMode>('files');
	const [sourceUrl, setSourceUrl] = useState('');
	const [files, setFiles] = useState<OrderedFile[]>([]);
	const [caption, setCaption] = useState('');
	const [soundUrl, setSoundUrl] = useState('');
	const [youtubeFormat, setYoutubeFormat] = useState<YouTubeFormat | ''>('');
	const [youtubeTitle, setYoutubeTitle] = useState('');
	const [youtubeDescription, setYoutubeDescription] = useState('');
	const [youtubeRegularConfirm, setYoutubeRegularConfirm] = useState(false);
	const [videoMeta, setVideoMeta] = useState<{ duration: number; width: number; height: number } | null>(null);
	const [perPlatformCaptions, setPerPlatformCaptions] = useState(false);
	const [platformCaptions, setPlatformCaptions] = useState<Partial<Record<ContentPlatform, string>>>({});
	const [platformSounds, setPlatformSounds] = useState<Partial<Record<ContentPlatform, string>>>({});
	const [returnHomeOnFinish, setReturnHomeOnFinish] = useState(true);
	const [slackBotId, setSlackBotId] = useState('');
	const [isDragging, setIsDragging] = useState(false);
	const [deviceSearch, setDeviceSearch] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Revoke preview object URLs on unmount so navigating away doesn't leak them.
	const filesRef = useRef<OrderedFile[]>([]);
	useEffect(() => {
		filesRef.current = files;
	}, [files]);
	useEffect(
		() => () => {
			filesRef.current.forEach((f) => URL.revokeObjectURL(f.previewUrl));
		},
		[],
	);

	// Read duration/dimensions so YouTube Shorts can be validated before start.
	useEffect(() => {
		if (contentType !== 'video' || files.length === 0) {
			setVideoMeta(null);
			return;
		}
		const video = document.createElement('video');
		video.preload = 'metadata';
		video.src = files[0].previewUrl;
		const onLoaded = () => {
			setVideoMeta({
				duration: Number.isFinite(video.duration) ? video.duration : 0,
				width: video.videoWidth,
				height: video.videoHeight,
			});
		};
		video.addEventListener('loadedmetadata', onLoaded);
		return () => {
			video.removeEventListener('loadedmetadata', onLoaded);
			video.removeAttribute('src');
			video.load();
		};
	}, [files, contentType]);

	// Reset the regular-video confirmation whenever the file or format changes.
	const firstFileId = files[0]?.id ?? '';
	useEffect(() => {
		setYoutubeRegularConfirm(false);
	}, [firstFileId, youtubeFormat, contentType]);


	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const { data: devicesData, isLoading: devicesLoading } = useQuery({
		queryKey: ['vmos-devices'],
		queryFn: fetchDevices,
	});

	const { data: slackBots = [] } = useQuery({
		queryKey: ['slack-bots'],
		queryFn: listSlackBots,
		staleTime: 60_000,
	});

	const devices = devicesData?.devices ?? [];

	const filteredDevices = useMemo(() => {
		const q = deviceSearch.toLowerCase();
		if (!q) return devices;
		return devices.filter(
			(d: any) =>
				(d.device_name?.toLowerCase() || '').includes(q) ||
				(d.pad_id?.toLowerCase() || '').includes(q) ||
				(d.platform?.toLowerCase() || '').includes(q),
		);
	}, [devices, deviceSearch]);

	const PLATFORM_LABELS: Record<ContentPlatform, string> = {
		tiktok: 'TikTok',
		facebook: 'FB',
		instagram: 'IG',
		youtube: 'YouTube',
	};

	// Normalize the device's stored platform into a content platform.
	const defaultPlatformForDevice = useCallback((device: VMOSDevice): ContentPlatform => {
		const stored = (device?.platform || '').toLowerCase();
		if (stored.includes('facebook') || stored.includes('fb')) return 'facebook';
		if (stored.includes('instagram') || stored.includes('ig')) return 'instagram';
		if (stored.includes('youtube') || stored === 'yt') return 'youtube';
		return 'tiktok';
	}, []);

	const toggleDevice = useCallback((device: VMOSDevice) => {
		const padId = device.pad_id;
		setPlatformsByDevice(prev => {
			const next = { ...prev };
			if (next[padId] && next[padId].length > 0) {
				delete next[padId];
			} else {
				next[padId] = [defaultPlatformForDevice(device)];
			}
			return next;
		});
	}, [defaultPlatformForDevice]);

	const toggleDevicePlatform = useCallback((padId: string, platform: ContentPlatform) => {
		setPlatformsByDevice(prev => {
			const cur = prev[padId] ?? [];
			const next = { ...prev };
			if (cur.includes(platform)) {
				const filtered = cur.filter(p => p !== platform);
				if (filtered.length === 0) delete next[padId];
				else next[padId] = filtered;
			} else {
				next[padId] = [...cur, platform];
			}
			// Multi-photo posts work on TikTok, Instagram, and Facebook.
			// Adding any other platform (e.g. YouTube) forces video.
			if (contentType === 'slideshow') {
				const all = Object.values(next).flat();
				if (all.some(p => p !== 'tiktok' && p !== 'instagram' && p !== 'facebook')) {
					setContentType('video');
					setFiles(prev => {
						prev.forEach(f => URL.revokeObjectURL(f.previewUrl));
						return [];
					});
				}
			}
			return next;
		});
	}, [contentType]);

	const toggleAll = useCallback(() => {
		setPlatformsByDevice(prev => {
			const hasAny = Object.values(prev).some(arr => arr.length > 0);
			if (hasAny) return {};
			const next: Record<string, ContentPlatform[]> = {};
			for (const d of devices) {
				next[d.pad_id] = [defaultPlatformForDevice(d)];
			}
			return next;
		});
	}, [devices, defaultPlatformForDevice]);

	const selectedDevices = useMemo(
		() => devices.filter((d: VMOSDevice) => (platformsByDevice[d.pad_id] ?? []).length > 0),
		[devices, platformsByDevice],
	);

	// Aggregate platforms actually in use, in tab order.
	const activePlatforms = useMemo(() => {
		const inUse = new Set<ContentPlatform>();
		for (const arr of Object.values(platformsByDevice)) {
			for (const p of arr) inUse.add(p);
		}
		return (['tiktok', 'facebook', 'instagram', 'youtube'] as ContentPlatform[]).filter(p => inUse.has(p));
	}, [platformsByDevice]);

	const countForPlatform = useCallback((platform: ContentPlatform) => {
		return Object.values(platformsByDevice).filter(arr => arr.includes(platform)).length;
	}, [platformsByDevice]);

	const canUseSlideshow =
		activePlatforms.length === 0 ||
		activePlatforms.every(p => p === 'tiktok' || p === 'instagram' || p === 'facebook');
	const multiPhotoLabel = activePlatforms.length === 0
		? 'Slideshow / Carousel'
		: activePlatforms.every(p => p === 'tiktok')
			? 'Slideshow'
			: activePlatforms.includes('tiktok')
				? 'Slideshow / Carousel'
				: 'Carousel';

	const isYouTubeActive = activePlatforms.includes('youtube');
	const nonYoutubePlatforms = activePlatforms.filter(p => p !== 'youtube');
	const youtubeVideoIsVertical = !!videoMeta && videoMeta.height > videoMeta.width;
	const youtubeVideoIsShortLength = !!videoMeta && videoMeta.duration > 0 && videoMeta.duration <= 180;
	const youtubeShortsInvalid =
		isYouTubeActive &&
		youtubeFormat === 'shorts' &&
		!!videoMeta &&
		(!youtubeVideoIsVertical || !youtubeVideoIsShortLength);
	const youtubeRegularNeedsConfirm =
		isYouTubeActive &&
		youtubeFormat === 'video' &&
		!!videoMeta &&
		youtubeVideoIsVertical &&
		youtubeVideoIsShortLength;
	const youtubeTitleTrimmed = youtubeTitle.trim();
	const youtubeFieldsValid =
		!isYouTubeActive ||
		(!!youtubeFormat &&
			youtubeTitleTrimmed.length > 0 &&
			youtubeTitleTrimmed.length <= 100 &&
			youtubeDescription.length <= 5000 &&
			!youtubeShortsInvalid &&
			(!youtubeRegularNeedsConfirm || youtubeRegularConfirm));

	const clearFiles = useCallback(() => {
		files.forEach(f => URL.revokeObjectURL(f.previewUrl));
		setFiles([]);
	}, [files]);

	const addFiles = useCallback((newFiles: File[]) => {
		const entries: OrderedFile[] = newFiles.map(file => ({
			id: crypto.randomUUID(),
			file,
			previewUrl: URL.createObjectURL(file),
			status: 'idle' as const,
		}));
		if (contentType === 'video') {
			setFiles(prev => {
				prev.forEach(f => URL.revokeObjectURL(f.previewUrl));
				return entries.slice(0, 1);
			});
		} else {
			setFiles(prev => [...prev, ...entries]);
		}
	}, [contentType]);

	const removeFile = useCallback((id: string) => {
		setFiles(prev => {
			const file = prev.find(f => f.id === id);
			if (file) URL.revokeObjectURL(file.previewUrl);
			return prev.filter(f => f.id !== id);
		});
	}, []);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			setFiles(prev => {
				const oldIndex = prev.findIndex(f => f.id === active.id);
				const newIndex = prev.findIndex(f => f.id === over.id);
				return arrayMove(prev, oldIndex, newIndex);
			});
		}
	}, []);

	const handleDrag = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDragIn = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
			setIsDragging(true);
		}
	}, []);

	const handleDragOut = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			addFiles(Array.from(e.dataTransfer.files));
		}
	}, [addFiles]);

	const setAllFileStatus = useCallback((status: FileStatus, error?: string) => {
		setFiles(prev => prev.map(f => ({ ...f, status, error })));
	}, []);

	const startMutation = useMutation({
		mutationFn: async () => {
			if (selectedDevices.length === 0) return;
			if (activePlatforms.length === 0) return;

			// 1) Ingest the media (browser files -> R2; Drive link -> R2).
			let refs: ContentFileRef[] = [];
			if (sourceMode === 'link') {
				const url = sourceUrl.trim();
				if (!url) return;
				const ingest = await ingestLink(url);
				refs = ingest.files;
			} else {
				if (files.length === 0) return;
				setAllFileStatus('uploading');
				try {
					const up = await uploadFiles(files.map(f => f.file));
					setAllFileStatus('done');
					refs = up.files;
				} catch (err) {
					setAllFileStatus('error', err instanceof Error ? err.message : 'Upload failed');
					throw err;
				}
			}

			// 2) Build one platform group per platform actually in use. Media is
			// pushed ONCE; the backend posts platform-by-platform sequentially.
			const groups = activePlatforms.map(platform => {
				const padIds = Object.entries(platformsByDevice)
					.filter(([, arr]) => arr.includes(platform))
					.map(([padId]) => padId);
				const overrideCaption = (platformCaptions[platform] ?? '').trim();
				const groupCaption = perPlatformCaptions && overrideCaption
					? overrideCaption
					: caption.trim();
				const overrideSound = (platformSounds[platform] ?? '').trim();
				const groupSound = perPlatformCaptions && overrideSound
					? overrideSound
					: soundUrl.trim();
				if (platform === 'youtube') {
					return {
						platform,
						pad_ids: padIds,
						title: youtubeTitleTrimmed,
						description: youtubeDescription.trim(),
						youtube_format: youtubeFormat || undefined,
					};
				}
				return {
					platform,
					pad_ids: padIds,
					...(groupCaption && { caption: groupCaption }),
					...(platform === 'tiktok' && groupSound && { sound_url: groupSound }),
				};
			});

			const data = await startMultiContentSession({
				groups,
				content_type: contentType,
				files: refs,
				...(!returnHomeOnFinish && { return_home_on_finish: false }),
				llm_model: DEFAULT_LLM_MODEL,
				comment_llm_model: DEFAULT_COMMENT_MODEL,
				...(slackBotId && { slack_bot_id: slackBotId }),
			});
			return data;
		},
		onSuccess: (data) => {
			if (!data?.group_id) return;
			const queued = data.groups.length - 1;
			navigate(`/runs/${data.group_id}?run=${data.run_id}`);
			toast.success(
				queued > 0
					? `Content session started — ${data.groups.length} platform${queued > 1 ? 's' : ''} queued, watching live`
					: 'Content session started — watching live',
			);
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : 'Failed to start content session');
		},
	});

	const isStarting = startMutation.isPending;
	const allIdle = files.every(f => f.status !== 'uploading');
	const canStart =
		selectedDevices.length > 0 &&
		activePlatforms.length > 0 &&
		!isStarting &&
		youtubeFieldsValid &&
		(sourceMode === 'link'
			? sourceUrl.trim().length > 0
			: files.length > 0 && allIdle);

	const switchSource = (mode: SourceMode) => {
		if (mode === sourceMode) return;
		if (mode === 'link' && contentType === 'slideshow') {
			setContentType('video');
			clearFiles();
		}
		setSourceMode(mode);
	};

	return (
		<AppShell>
			<PageHeader>
					<SidebarTrigger>
						<PanelLeftOpen className="h-4 w-4" />
					</SidebarTrigger>
					<div className="flex flex-col">
						<h1 className="text-lg font-semibold leading-tight">Content</h1>
						<p className="text-xs text-muted-foreground">
							Post videos, slideshows & carousels — starts a live agent session per device.
						</p>
					</div>
			</PageHeader>

			<div className="flex-1 min-h-0 overflow-y-auto">
				<div className="max-w-4xl mx-auto px-6 py-6 space-y-8">
					{/* Devices + platform assignment */}
					<section className="space-y-3">
						<div className="flex items-center justify-between">
							<div className="flex items-baseline gap-3 flex-wrap">
								<span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
									1 · Devices & platforms
								</span>
								{selectedDevices.length > 0 && (
									<span className="text-xs font-mono text-muted-foreground">
										{selectedDevices.length} of {devices.length} selected
									</span>
								)}
								{activePlatforms.length > 0 && (
									<span className="text-xs font-mono text-primary">
										{activePlatforms.map(p => `${PLATFORM_LABELS[p]} ×${countForPlatform(p)}`).join(' · ')}
									</span>
								)}
							</div>
							{devices.length > 0 && (
								<Button variant="ghost" size="sm" onClick={toggleAll}>
									{selectedDevices.length === devices.length ? 'Deselect all' : 'Select all'}
								</Button>
							)}
						</div>

						<p className="text-xs text-muted-foreground">
							Pick the device(s) and toggle which platform each posts to. A device can post to
							multiple platforms — the same content runs on each platform in order, one at a time.
						</p>

						<div className="relative">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
							<Input
								placeholder="Search devices..."
								value={deviceSearch}
								onChange={(e) => setDeviceSearch(e.target.value)}
								className="pl-8 h-8 text-xs"
							/>
						</div>

						{devicesLoading ? (
							<div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
								Loading devices…
							</div>
						) : devices.length === 0 ? (
							<div className="flex flex-col items-center gap-1.5 py-12 text-center border rounded-lg">
								<Smartphone className="h-7 w-7 text-muted-foreground/40" />
								<p className="text-sm font-medium">No devices found</p>
								<p className="text-xs text-muted-foreground">Configure devices first.</p>
							</div>
						) : (
							<div className="border rounded-lg divide-y">
								{filteredDevices.length === 0 ? (
									<div className="py-8 text-center text-sm text-muted-foreground">
										No devices match your search
									</div>
								) : (
									filteredDevices.map((device: any) => {
										const padPlatforms = platformsByDevice[device.pad_id] ?? [];
										const selected = padPlatforms.length > 0;
										return (
											<div
												key={device.pad_id}
												className={cn(
													"px-4 py-2.5 transition-colors",
													selected && "bg-primary/5",
												)}
											>
												<div className="flex items-center gap-3 w-full text-left">
													<Checkbox
														checked={selected}
														onCheckedChange={() => toggleDevice(device)}
													/>
													<Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
													<span className="font-medium text-sm truncate">{device.device_name}</span>
													{device.username && (
														<span className="text-xs text-muted-foreground font-mono shrink-0">@{device.username}</span>
													)}
													{device.busy && (
														<span
															className="text-[10px] uppercase tracking-wide text-amber-600 border border-amber-500/40 rounded px-1.5 py-0.5 shrink-0"
															title={`In use by ${device.locked_by || 'another run'}`}
														>
															busy
														</span>
													)}
												</div>
												{selected && (
													<div className="flex items-center gap-1.5 mt-2 pl-7 flex-wrap">
														{(['tiktok', 'facebook', 'instagram', 'youtube'] as ContentPlatform[]).map(platform => {
															const on = padPlatforms.includes(platform);
															return (
																<button
																	type="button"
																	key={platform}
																	onClick={(e) => { e.stopPropagation(); toggleDevicePlatform(device.pad_id, platform); }}
																	className={cn(
																		"text-[11px] font-medium px-2 py-1 rounded border transition-colors",
																		on
																			? "bg-primary text-primary-foreground border-primary"
																			: "text-muted-foreground border-input hover:border-primary/50",
																	)}
																>
																	{PLATFORM_LABELS[platform]}
																</button>
															);
														})}
													</div>
												)}
											</div>
										);
									})
								)}
							</div>
						)}
					</section>

					{/* Media */}
					<section className="space-y-3">
						<div className="flex items-center justify-between">
							<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
								2 · Media
							</div>
							<Tabs value={sourceMode} onValueChange={(v) => switchSource(v as SourceMode)}>
								<TabsList className="h-7">
									<TabsTrigger value="files" className="gap-1.5 text-xs">
										<Upload className="h-3.5 w-3.5" />
										Upload files
									</TabsTrigger>
								<TabsTrigger value="link" className="gap-1.5 text-xs">
									<Link2 className="h-3.5 w-3.5" />
									Public link
								</TabsTrigger>
								</TabsList>
							</Tabs>
						</div>

						{sourceMode === 'link' ? (
							<div className="space-y-2">
								<Input
									type="url"
									placeholder="https://drive.google.com/file/d/<FILE_ID>/view or https://<bucket>.r2.dev/video.mp4"
									value={sourceUrl}
									onChange={(e) => setSourceUrl(e.target.value)}
								/>
								<p className="text-xs text-muted-foreground">
									Paste a public link to a single file — Google Drive (shared as{' '}
									<em>"Anyone with the link"</em>), Cloudflare R2, Supabase Storage, S3,
									Dropbox, or any other direct public URL. The backend downloads it and
									pushes it to each selected device. Videos recommended (single file, max
									512 MB).
								</p>
							</div>
						) : (
							<div className="space-y-3">
								<Tabs value={contentType} onValueChange={(v) => { setContentType(v as ContentType); clearFiles(); }}>
									<TabsList>
										<TabsTrigger value="video" className="gap-1.5">
											<Film className="h-4 w-4" />
											Video
										</TabsTrigger>
										{canUseSlideshow && (
											<TabsTrigger value="slideshow" className="gap-1.5">
												<Images className="h-4 w-4" />
												{multiPhotoLabel}
											</TabsTrigger>
										)}
									</TabsList>

									<TabsContent value="video" className="space-y-4 mt-4">
										<div className="space-y-2">
											<input
												ref={fileInputRef}
												type="file"
												accept="video/*"
												className="hidden"
												onChange={(e) => {
													if (e.target.files?.length) addFiles(Array.from(e.target.files));
													e.target.value = '';
												}}
											/>
											{files.length === 0 ? (
												<button
													type="button"
													onClick={() => fileInputRef.current?.click()}
													onDragEnter={handleDragIn}
													onDragLeave={handleDragOut}
													onDragOver={handleDrag}
													onDrop={handleDrop}
													className={cn(
														'w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors',
														isDragging
															? 'border-lime-fill bg-lime-soft'
															: 'border-input hover:border-lime-fill/50',
													)}
												>
													<Film className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
													<p className="text-sm text-muted-foreground">
														{isDragging ? 'Drop video here' : 'Click or drag a video'}
													</p>
												</button>
											) : (
												<div className="flex items-center gap-3 p-3 border rounded-lg">
													<FileStatusIcon status={files[0].status} />
													<span className="text-sm truncate flex-1">{files[0].file.name}</span>
													<span className="text-xs text-muted-foreground">
														{(files[0].file.size / 1024 / 1024).toFixed(1)} MB
													</span>
													{files[0].status !== 'uploading' && (
														<button onClick={() => removeFile(files[0].id)} className="text-muted-foreground hover:text-destructive">
															<X className="h-4 w-4" />
														</button>
													)}
												</div>
											)}
										</div>
									</TabsContent>

									<TabsContent value="slideshow" className="space-y-4 mt-4">
										<div className="space-y-2">
											<input
												ref={fileInputRef}
												type="file"
												accept="image/*"
												multiple
												className="hidden"
												onChange={(e) => {
													if (e.target.files?.length) addFiles(Array.from(e.target.files));
													e.target.value = '';
												}}
											/>
											<button
												type="button"
												onClick={() => fileInputRef.current?.click()}
												onDragEnter={handleDragIn}
												onDragLeave={handleDragOut}
												onDragOver={handleDrag}
												onDrop={handleDrop}
												className={cn(
													'w-full border-2 border-dashed rounded-lg p-4 text-center transition-colors',
													isDragging
														? 'border-lime-fill bg-lime-soft'
														: 'border-input hover:border-lime-fill/50',
												)}
											>
												<Images className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
												<p className="text-sm text-muted-foreground">
													{isDragging
														? 'Drop images here'
														: files.length === 0
															? 'Click or drag images'
															: 'Click or drag to add more'}
												</p>
											</button>

											{files.length > 0 && (
												<div className="space-y-1">
													<p className="text-xs text-muted-foreground">
														Slide 1 = first in list (appears top-left in the gallery). Drag to reorder.
													</p>
													<DndContext
														sensors={sensors}
														collisionDetection={closestCenter}
														onDragEnd={handleDragEnd}
													>
														<SortableContext
															items={files.map(f => f.id)}
															strategy={verticalListSortingStrategy}
														>
															{files.map((file, index) => (
																<SortableImageRow
																	key={file.id}
																	file={file}
																	index={index}
																	isUploading={isStarting}
																	onRemove={removeFile}
																/>
															))}
														</SortableContext>
													</DndContext>
												</div>
											)}
										</div>
									</TabsContent>
								</Tabs>
							</div>
						)}
					</section>

					{/* Caption / YouTube details */}
					<section className="space-y-3">
						<div className="flex items-center justify-between">
							<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
								4 · Caption & YouTube details
							</div>
							{nonYoutubePlatforms.length > 1 && (
								<div className="flex items-center gap-2">
									<Switch
										id="per-platform-captions"
										checked={perPlatformCaptions}
										onCheckedChange={setPerPlatformCaptions}
									/>
									<label htmlFor="per-platform-captions" className="text-xs text-muted-foreground cursor-pointer">
										Different captions per platform
									</label>
								</div>
							)}
						</div>

						{nonYoutubePlatforms.length > 0 && (
							perPlatformCaptions ? (
								<div className="space-y-3">
									{nonYoutubePlatforms.map(platform => (
										<div key={platform} className="space-y-1.5 border rounded-lg p-3">
											<label className="text-xs font-medium">{PLATFORM_LABELS[platform]} caption</label>
											<Textarea
												placeholder={`Caption for ${PLATFORM_LABELS[platform]}... e.g. vibes in tokyo #travel #fyp`}
												value={platformCaptions[platform] ?? ''}
												onChange={(e) => setPlatformCaptions(prev => ({ ...prev, [platform]: e.target.value }))}
												className="min-h-[70px]"
											/>
											{platform === 'tiktok' && (
												<div className="space-y-1.5">
													<label className="text-xs text-muted-foreground">TikTok sound (optional)</label>
													<Input
														type="url"
														placeholder="Paste a TikTok video link to use its sound"
														value={platformSounds.tiktok ?? ''}
														onChange={(e) => setPlatformSounds(prev => ({ ...prev, tiktok: e.target.value }))}
													/>
												</div>
											)}
										</div>
									))}
								</div>
							) : (
								<>
									<Textarea
										placeholder="Write your caption with hashtags, just like on TikTok... e.g. vibes in tokyo #travel #fyp"
										value={caption}
										onChange={(e) => setCaption(e.target.value)}
										className="min-h-[80px]"
									/>
									{activePlatforms.includes('tiktok') && (
										<div className="space-y-1.5">
											<label className="text-xs text-muted-foreground">TikTok sound (optional)</label>
											<Input
												type="url"
												placeholder="Paste a TikTok video link to use its sound"
												value={soundUrl}
												onChange={(e) => setSoundUrl(e.target.value)}
											/>
										</div>
									)}
								</>
							)
						)}

						{isYouTubeActive && (
							<div className="space-y-3 border rounded-lg p-3">
								<div className="flex items-center justify-between">
									<div className="text-xs font-medium">YouTube</div>
									<span className="text-[11px] text-muted-foreground">Public · Not made for kids</span>
								</div>

								<div className="space-y-1.5">
									<label className="text-xs text-muted-foreground">Format (required)</label>
									<Select
										value={youtubeFormat || 'none'}
										onValueChange={(v) => setYoutubeFormat(v === 'none' ? '' : (v as YouTubeFormat))}
									>
										<SelectTrigger><SelectValue /></SelectTrigger>
										<SelectContent>
											<SelectItem value="none">Select Shorts or regular video</SelectItem>
											<SelectItem value="shorts">Shorts (vertical, 3 min or less)</SelectItem>
											<SelectItem value="video">Regular video</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-1.5">
									<div className="flex items-center justify-between">
										<label className="text-xs text-muted-foreground">Title (required)</label>
										<span className="text-[11px] text-muted-foreground">{youtubeTitleTrimmed.length}/100</span>
									</div>
									<Input
										value={youtubeTitle}
										onChange={(e) => setYoutubeTitle(e.target.value)}
										maxLength={100}
										placeholder="Short, catchy YouTube title"
									/>
								</div>

								<div className="space-y-1.5">
									<div className="flex items-center justify-between">
										<label className="text-xs text-muted-foreground">Description</label>
										<span className="text-[11px] text-muted-foreground">{youtubeDescription.length}/5000</span>
									</div>
									<Textarea
										value={youtubeDescription}
										onChange={(e) => setYoutubeDescription(e.target.value)}
										maxLength={5000}
										placeholder="YouTube description (optional)"
										className="min-h-[80px]"
									/>
								</div>

								{!videoMeta && (
									<p className="text-[11px] text-muted-foreground">
										{sourceMode === 'link'
											? 'For link uploads, the agent verifies the published format after upload.'
											: 'Video metadata will be checked once the file is selected.'}
									</p>
								)}
								{youtubeShortsInvalid && (
									<p className="text-xs text-destructive">
										Shorts require a vertical video (taller than wide) that is 3 minutes or less.
									</p>
								)}
								{youtubeRegularNeedsConfirm && (
									<div className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/5 p-2">
										<Checkbox
											id="youtube-regular-confirm"
											checked={youtubeRegularConfirm}
											onCheckedChange={(v) => setYoutubeRegularConfirm(v === true)}
										/>
										<label htmlFor="youtube-regular-confirm" className="text-xs text-amber-700 leading-snug cursor-pointer">
											This video is vertical and under 3 minutes. YouTube may publish it as a Short. I confirm this should be a regular video and understand the agent will report a mismatch if it lands as a Short.
										</label>
									</div>
								)}
							</div>
						)}

						<p className="text-xs text-muted-foreground">
							Captions apply to TikTok, Instagram, and Facebook. YouTube uses the title and description above.
							TikTok sound is only used on TikTok.
						</p>
					</section>

					{/* Run options */}
					<section className="space-y-3">
						<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
							5 · Session options
						</div>
						<div className="flex items-center gap-2">
							<Switch
								id="return-home"
								checked={returnHomeOnFinish}
								onCheckedChange={setReturnHomeOnFinish}
							/>
							<label htmlFor="return-home" className="text-xs text-muted-foreground cursor-pointer">
								Return device to home screen when the session finishes
							</label>
						</div>

						{slackBots.length > 0 && (
							<div className="space-y-1.5">
								<label className="text-xs text-muted-foreground">Slack notification</label>
								<Select value={slackBotId || 'none'} onValueChange={(v) => setSlackBotId(v === 'none' ? '' : v)}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">No notification</SelectItem>
										{slackBots.filter((b: SlackBot) => b.enabled).map(b => (
											<SelectItem key={b.id} value={b.id}>
												{b.name} {b.channel_name ? `(${b.channel_name})` : ''}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
					</section>

					{/* Start Session */}
					<Button
						onClick={() => startMutation.mutate()}
						disabled={!canStart}
						className="w-full"
						size="lg"
					>
						{isStarting ? (
							<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting session on {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''}...</>
						) : (
							<><Rocket className="h-4 w-4 mr-2" /> Start posting on {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''} · {activePlatforms.length} platform{activePlatforms.length !== 1 ? 's' : ''}</>
						)}
					</Button>

					<p className="text-xs text-muted-foreground text-center -mt-4 flex items-center justify-center gap-1.5">
						<Play className="h-3 w-3" />
						Starts a live agent session — when multiple platforms are selected, content posts on
						one platform at a time and auto-advances to the next as each finishes.
					</p>
				</div>
			</div>
		</AppShell>
	);
}

// === Helpers ===

function FileStatusIcon({ status }: { status: FileStatus }) {
	switch (status) {
		case 'uploading':
			return <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />;
		case 'done':
			return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
		case 'error':
			return <AlertCircle className="h-4 w-4 text-destructive shrink-0" />;
		default:
			return null;
	}
}
